// ./src/components/generic/form/TimezoneSelect.tsx
//
// Reusable IANA timezone picker — carries the full timezone list internally
// (browser-provided via Intl.supportedValuesOf, with a small fallback) so a
// caller just drops it in. Uses the shared `(name, value)` change contract.

import React, { useMemo } from 'react';

import { SelectField, SelectFieldProps } from './SelectField';

/** The user's current IANA timezone, or "UTC" if it can't be resolved. */
export const detectBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const FALLBACK_ZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Australia/Sydney',
];

const TIMEZONE_VALUES: string[] = (() => {
  try {
    const supported = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] })
      .supportedValuesOf;
    if (supported) return supported('timeZone');
  } catch {
    /* fall through */
  }
  return FALLBACK_ZONES;
})();

const TIMEZONE_OPTIONS = TIMEZONE_VALUES.map((z) => ({ value: z, label: z.replace(/_/g, ' ') }));

export interface TimezoneSelectProps
  extends Omit<SelectFieldProps, 'data' | 'searchable' | 'allowDeselect'> {}

export const TimezoneSelect: React.FC<TimezoneSelectProps> = (props) => {
  const data = useMemo(() => TIMEZONE_OPTIONS, []);
  return (
    <SelectField
      label="Timezone"
      placeholder="Select a timezone"
      nothingFoundMessage="No match"
      {...props}
      data={data}
      searchable
      allowDeselect={false}
    />
  );
};
